import { AxiosError } from "axios";

type StructuredErrorDetail = {
  code?: string;
  error_msg?: string;
};

const getDetail = (error: unknown): unknown => {
  if (!(error instanceof AxiosError)) return undefined;
  return (error.response?.data as { detail?: unknown } | undefined)?.detail;
};

const getStructuredErrorMsg = (detail: unknown): string | undefined => {
  if (typeof detail !== "object" || detail === null) return undefined;
  const errorMsg = (detail as StructuredErrorDetail).error_msg;
  return typeof errorMsg === "string" && errorMsg.length > 0
    ? errorMsg
    : undefined;
};

/**
 * Reads the backend-provided, human-readable error message for an SDK Bundle
 * operation. The backend is the single source of truth: intentional 4xx errors
 * carry a structured `detail` of the shape `{ code, error_msg }`.
 *
 * Falls back to `fallback` for anything unstructured (network errors, 5xx, or
 * responses without an `error_msg`). A plain string `detail` is returned
 * defensively to stay compatible with non-migrated responses.
 */
export const getSdkBundleErrorDescription = (
  error: unknown,
  fallback: string,
): string => {
  const detail = getDetail(error);

  const errorMsg = getStructuredErrorMsg(detail);
  if (errorMsg !== undefined) return errorMsg;

  if (typeof detail === "string" && detail.length > 0) return detail;

  return fallback;
};
